<template>
  <div>
    <b-row class="mb-2">
      <b-col cols="12" md="6">
        <b-form-group
          :label="$t('efficiencyTests.bubbleChart.xAxisDriver')"
          label-for="bubble-x-driver"
          label-size="sm"
        >
          <b-form-select id="bubble-x-driver" v-model="xDriver" :options="xOptions" size="sm"></b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6">
        <b-form-group
          :label="$t('efficiencyTests.bubbleChart.yAxisDriver')"
          label-for="bubble-y-driver"
          label-size="sm"
        >
          <b-form-select id="bubble-y-driver" v-model="yDriver" :options="yOptions" size="sm"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>
    <highcharts v-if="series.length > 0" :key="hcKey" :options="chartOptions"></highcharts>
    <chart-not-available v-else :if-is-mop="false"></chart-not-available>
  </div>
</template>

<script>
import { setChartMenuItemsLang } from '../../utils/formatter'
import { formatScorePercent, getBubbleSeries, getDefaultAxesDrivers } from '../../utils/driversBubbleChart'
import ChartNotAvailable from '../MarketCompCharts/ChartNotAvailable'

// "Confronto driver" view of EfficiencyTestsDetails.vue, alternative to the score distribution
// histogram (Insight.vue). Same Highcharts bubble set-up as Search/Charts/ReturnRiskCost.vue, kept
// separate so the search page is not affected.
export default {
  name: 'DriversBubbleChart',
  components: {
    ChartNotAvailable,
  },
  props: {
    // summary.drivers of the result: [{ driver, weight, ... }]
    drivers: {
      type: Array,
      default: () => [],
    },
    testedProduct: {
      type: Object,
      default: null,
    },
    comparables: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      hcKey: 0,
      xDriver: null,
      yDriver: null,
    }
  },
  computed: {
    driverOptions() {
      return this.drivers
        .filter((d) => d && d.driver)
        .map((d) => ({ value: d.driver, text: this.driverLabel(d.driver) }))
    },
    // The same driver can't be on both axes.
    xOptions() {
      return this.driverOptions.map((o) => ({ ...o, disabled: o.value === this.yDriver }))
    },
    yOptions() {
      return this.driverOptions.map((o) => ({ ...o, disabled: o.value === this.xDriver }))
    },
    series() {
      return getBubbleSeries(this.testedProduct, this.comparables, this.xDriver, this.yDriver)
    },
    setOptions() {
      this.hcKey += 1
      return setChartMenuItemsLang()
    },
    chartOptions() {
      const percentLabel = (el) => formatScorePercent(el.value)
      const scoreLabel = this.$t('efficiencyTests.bubbleChart.score')
      return {
        lang: this.setOptions,
        chart: {
          type: 'bubble',
          zoomType: 'xy',
          backgroundColor: '#FFFFFF',
          height: '42%',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          filename: this.$t('efficiencyTests.bubbleChart.title'),
        },
        title: {
          text: this.$t('efficiencyTests.bubbleChart.title'),
        },
        plotOptions: {
          bubble: {
            minSize: 8,
            maxSize: 40,
            zMin: 0,
            zMax: 1,
          },
        },
        // On the left (clear of the export menu, top right); its width also narrows the plot area.
        legend: {
          enabled: true,
          align: 'left',
          layout: 'vertical',
          verticalAlign: 'middle',
          width: '18%',
          maxHeight: 300,
          itemMarginTop: 4,
          itemStyle: { fontWeight: 'normal', textOverflow: 'ellipsis' },
          bubbleLegend: {
            enabled: false,
          },
        },
        tooltip: {
          formatter: function () {
            const meta = this.point.options
            return (
              '<b>' +
              meta.isin +
              '</b><br>' +
              meta.issuerName +
              '<br>' +
              meta.name +
              '<br>' +
              scoreLabel +
              ': ' +
              formatScorePercent(meta.z)
            )
          },
        },
        xAxis: {
          gridLineWidth: 1,
          title: { text: this.driverLabel(this.xDriver) },
          labels: { formatter: percentLabel },
        },
        yAxis: {
          title: { text: this.driverLabel(this.yDriver) },
          labels: { formatter: percentLabel },
        },
        // `marker` must be left out (not set to undefined) for the comparables: Highcharts reads marker.symbol.
        series: this.series.map(({ isTestedProduct, ...s }) =>
          isTestedProduct ? { ...s, marker: { lineColor: '#000000', lineWidth: 1 } } : s
        ),
      }
    },
  },
  watch: {
    drivers: {
      immediate: true,
      handler(drivers) {
        const defaults = getDefaultAxesDrivers(drivers)
        this.xDriver = defaults.x
        this.yDriver = defaults.y
      },
    },
  },
  methods: {
    driverLabel(driver) {
      return driver ? this.$t(`drivers.categories.${driver.toUpperCase()}`) : ''
    },
  },
}
</script>
